import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';
import { DollarSign, TrendingUp, TrendingDown, Plus, Trash2, Filter } from 'lucide-react';

export default function Finance() {
  const { user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  
  const [formData, setFormData] = useState({
    type: 'expense',
    amount: '',
    description: '',
    category: ''
  });

  const categories = {
    income: ['Salary', 'Freelance', 'Investment', 'Other'],
    expense: ['Food', 'Transport', 'Utilities', 'Entertainment', 'Shopping', 'Health', 'Other']
  };

  useEffect(() => {
    fetchEntries();
  }, [selectedMonth, selectedYear]);

  const fetchEntries = async () => {
    setLoading(true);
    try {
      const startDate = new Date(selectedYear, selectedMonth, 1);
      const endDate = new Date(selectedYear, selectedMonth + 1, 0, 23, 59, 59);

      const { data, error } = await supabase
        .from('finance_entries')
        .select('*')
        .eq('user_id', user.id)
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString())
        .order('created_at', { ascending: false });

      if (error) throw error;
      setEntries(data || []);
    } catch (error) {
      console.error('Error fetching entries:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.amount || !formData.description || !formData.category) return;

    try {
      const { error } = await supabase.from('finance_entries').insert({
        user_id: user.id,
        type: formData.type,
        amount: parseFloat(formData.amount),
        description: formData.description,
        category: formData.category
      });

      if (error) throw error;
      setFormData({ type: 'expense', amount: '', description: '', category: '' });
      setShowForm(false);
      fetchEntries();
    } catch (error) {
      console.error('Error adding entry:', error);
    }
  };

  const handleDelete = async (id) => {
    try {
      await supabase.from('finance_entries').delete().eq('id', id);
      setEntries((prev) => prev.filter((e) => e.id !== id));
    } catch (error) {
      console.error('Error deleting entry:', error);
    }
  };

  const totalIncome = entries
    .filter((e) => e.type === 'income')
    .reduce((sum, e) => sum + Number(e.amount), 0);

  const totalExpenses = entries
    .filter((e) => e.type === 'expense')
    .reduce((sum, e) => sum + Number(e.amount), 0);

  const netBalance = totalIncome - totalExpenses;

  const categoryBreakdown = entries
    .filter((e) => e.type === 'expense')
    .reduce((acc, e) => {
      acc[e.category] = (acc[e.category] || 0) + Number(e.amount);
      return acc;
    }, {});

  const maxCategoryAmount = Math.max(...Object.values(categoryBreakdown), 1);

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center pb-20">
        <div className="text-indigo-400 font-mono animate-pulse">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0f] pb-20" data-testid="finance-screen">
      {/* Header */}
      <div className="p-4 border-b border-[#2a2a3a] flex items-center justify-between">
        <div className="flex items-center gap-3">
          <DollarSign className="w-5 h-5 text-green-400" />
          <h1 className="font-mono text-lg font-bold text-[#f0f0f8]">Finance</h1>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className="bg-indigo-600 hover:bg-indigo-500 text-white p-2 rounded-sm transition-colors"
          data-testid="add-entry-btn"
        >
          <Plus className="w-5 h-5" />
        </button>
      </div>

      {/* Month Selector */}
      <div className="p-4 border-b border-[#2a2a3a] flex gap-2">
        <select
          value={selectedMonth}
          onChange={(e) => setSelectedMonth(Number(e.target.value))}
          className="flex-1 bg-[#111118] border border-[#2a2a3a] text-[#f0f0f8] rounded-sm px-3 py-2 text-sm"
          data-testid="month-select"
        >
          {months.map((month, idx) => (
            <option key={month} value={idx}>{month}</option>
          ))}
        </select>
        <select
          value={selectedYear}
          onChange={(e) => setSelectedYear(Number(e.target.value))}
          className="bg-[#111118] border border-[#2a2a3a] text-[#f0f0f8] rounded-sm px-3 py-2 text-sm"
          data-testid="year-select"
        >
          {[2024, 2025, 2026].map((year) => (
            <option key={year} value={year}>{year}</option>
          ))}
        </select>
      </div>

      {/* Add Entry Form */}
      {showForm && (
        <form onSubmit={handleSubmit} className="p-4 border-b border-[#2a2a3a] space-y-3" data-testid="entry-form">
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setFormData({ ...formData, type: 'income', category: '' })}
              className={`flex-1 py-2 rounded-sm font-mono text-xs uppercase ${
                formData.type === 'income'
                  ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                  : 'bg-[#16161e] text-[#8888aa] border border-[#2a2a3a]'
              }`}
            >
              Income
            </button>
            <button
              type="button"
              onClick={() => setFormData({ ...formData, type: 'expense', category: '' })}
              className={`flex-1 py-2 rounded-sm font-mono text-xs uppercase ${
                formData.type === 'expense'
                  ? 'bg-red-500/20 text-red-400 border border-red-500/30'
                  : 'bg-[#16161e] text-[#8888aa] border border-[#2a2a3a]'
              }`}
            >
              Expense
            </button>
          </div>
          <input
            type="number"
            value={formData.amount}
            onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
            placeholder="Amount"
            className="w-full bg-[#111118] border border-[#2a2a3a] text-[#f0f0f8] rounded-sm px-3 py-2 text-sm"
            step="0.01"
            data-testid="amount-input"
          />
          <input
            type="text"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Description"
            className="w-full bg-[#111118] border border-[#2a2a3a] text-[#f0f0f8] rounded-sm px-3 py-2 text-sm"
            data-testid="description-input"
          />
          <select
            value={formData.category}
            onChange={(e) => setFormData({ ...formData, category: e.target.value })}
            className="w-full bg-[#111118] border border-[#2a2a3a] text-[#f0f0f8] rounded-sm px-3 py-2 text-sm"
            data-testid="category-select"
          >
            <option value="">Select category</option>
            {categories[formData.type].map((cat) => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
          <button
            type="submit"
            className="w-full bg-indigo-600 hover:bg-indigo-500 text-white font-mono text-xs uppercase py-2 rounded-sm"
            data-testid="save-entry-btn"
          >
            Save Entry
          </button>
        </form>
      )}

      {/* Summary Cards */}
      <div className="p-4 grid grid-cols-3 gap-3" data-testid="summary-cards">
        <div className="bg-[#16161e] border border-[#2a2a3a] rounded-sm p-3">
          <div className="flex items-center gap-1 mb-1">
            <TrendingUp className="w-3 h-3 text-green-400" />
            <p className="font-mono text-[10px] text-[#8888aa] uppercase">Income</p>
          </div>
          <p className="text-lg font-bold text-green-400">${totalIncome.toFixed(2)}</p>
        </div>
        <div className="bg-[#16161e] border border-[#2a2a3a] rounded-sm p-3">
          <div className="flex items-center gap-1 mb-1">
            <TrendingDown className="w-3 h-3 text-red-400" />
            <p className="font-mono text-[10px] text-[#8888aa] uppercase">Expenses</p>
          </div>
          <p className="text-lg font-bold text-red-400">${totalExpenses.toFixed(2)}</p>
        </div>
        <div className="bg-[#16161e] border border-[#2a2a3a] rounded-sm p-3">
          <div className="flex items-center gap-1 mb-1">
            <DollarSign className="w-3 h-3 text-indigo-400" />
            <p className="font-mono text-[10px] text-[#8888aa] uppercase">Net</p>
          </div>
          <p className={`text-lg font-bold ${netBalance >= 0 ? 'text-green-400' : 'text-red-400'}`}>
            ${netBalance.toFixed(2)}
          </p>
        </div>
      </div>

      {/* Category Breakdown */}
      {Object.keys(categoryBreakdown).length > 0 && (
        <div className="px-4 mb-4" data-testid="category-breakdown">
          <h2 className="font-mono text-xs text-[#8888aa] uppercase tracking-wider mb-3">Expense Breakdown</h2>
          <div className="space-y-2">
            {Object.entries(categoryBreakdown).map(([cat, amount]) => (
              <div key={cat}>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-[#f0f0f8]">{cat}</span>
                  <span className="text-[#8888aa]">${amount.toFixed(2)}</span>
                </div>
                <div className="h-2 bg-[#16161e] rounded-sm overflow-hidden">
                  <div
                    className="h-full bg-indigo-500"
                    style={{ width: `${(amount / maxCategoryAmount) * 100}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Entries List */}
      <div className="px-4 space-y-2" data-testid="entries-list">
        <h2 className="font-mono text-xs text-[#8888aa] uppercase tracking-wider mb-3">Transactions</h2>
        {entries.length === 0 ? (
          <div className="text-center py-8 text-[#555570]">
            No transactions this month
          </div>
        ) : (
          entries.map((entry) => (
            <div
              key={entry.id}
              className="bg-[#16161e] border border-[#2a2a3a] rounded-sm p-3 flex items-center justify-between"
              data-testid={`entry-${entry.id}`}
            >
              <div>
                <p className="text-sm text-[#f0f0f8]">{entry.description}</p>
                <p className="text-[10px] text-[#555570]">{entry.category}</p>
              </div>
              <div className="flex items-center gap-3">
                <span className={`font-mono text-sm font-bold ${
                  entry.type === 'income' ? 'text-green-400' : 'text-red-400'
                }`}>
                  {entry.type === 'income' ? '+' : '-'}${Number(entry.amount).toFixed(2)}
                </span>
                <button
                  onClick={() => handleDelete(entry.id)}
                  className="text-[#555570] hover:text-red-400 transition-colors"
                  data-testid={`delete-entry-${entry.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
